import { useState, useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuthStore } from '@/store/auth-store'
import { PageLoader } from '@/components/ui/page-loader'
import { Pagination } from '@/components/ui/pagination'
import { TruncationNotice } from '@/components/ui/truncation-notice'
import { PortalDropdown } from '@/components/ui/portal-dropdown'
import { isRevisedProposal } from '@/lib/list-filters'
import { revisionCount } from '@/lib/revision-label'
import { proposalsService } from '@/services/proposals-service'
import { estimatesService } from '@/services/estimates-service'
import type { ProposalRecord } from '@/types'

const PAGE_SIZE = 25

type ProposalStatus = 'signed' | 'viewed' | 'sent' | 'expired' | 'draft'
type TabKey = 'all' | 'pending_approval' | 'sent' | 'viewed' | 'signed' | 'expired' | 'revised'

const STATUS_LABELS: Record<ProposalStatus, string> = {
  signed: 'Signed',
  viewed: 'Viewed',
  sent: 'Sent',
  expired: 'Expired',
  draft: 'Draft',
}

const TAB_CONFIG: { key: TabKey; label: string }[] = [
  { key: 'all', label: 'All Proposals' },
  { key: 'pending_approval', label: 'Submitted for Approval' },
  { key: 'sent', label: 'Sent' },
  { key: 'viewed', label: 'Viewed' },
  { key: 'signed', label: 'Signed' },
  { key: 'expired', label: 'Expired' },
  // Not a status like the rest — see isRevisedProposal. A revised proposal keeps
  // whatever status it holds now, so it shows under this tab AND its status tab.
  { key: 'revised', label: 'Revised' },
]

/** Map backend status to display status */
function mapStatus(record: ProposalRecord): ProposalStatus {
  const s = record.status
  if (s === 'signed') return 'signed'
  if (s === 'viewed') return 'viewed'
  if (s === 'sent_to_client' || s === 'sent') return 'sent'
  // Check expiry for sent/viewed proposals
  if (record.expiresAt) {
    const deadline = new Date(record.expiresAt)
    if (new Date() > deadline && s !== 'signed') return 'expired'
  }
  return 'draft' // draft, approved, etc. → show as draft/pending
}

function formatDate(dateStr?: string | null): string {
  if (!dateStr) return '—'
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
}

function formatDateShort(dateStr?: string | null): string {
  if (!dateStr) return '—'
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

export function ProposalsListPage() {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<TabKey>('all')
  const [search, setSearch] = useState('')
  const [proposals, setProposals] = useState<ProposalRecord[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [sendModalOpen, setSendModalOpen] = useState(false)
  const [sendingProposal, setSendingProposal] = useState<ProposalRecord | null>(null)
  const [isSending, setIsSending] = useState(false)
  const [emailRecipients, setEmailRecipients] = useState<string[]>([])
  const [pendingOpen, setPendingOpen] = useState(true)
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [take, setTake] = useState(1000)
  const [loadingAll, setLoadingAll] = useState(false)
  const [searchParams] = useSearchParams()

  // Row action menu state
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  // Mark as Signed confirmation + dialog state
  const [markSignedConfirmOpen, setMarkSignedConfirmOpen] = useState(false)
  const [markSignedConfirmRecord, setMarkSignedConfirmRecord] = useState<ProposalRecord | null>(null)
  const [markSignedOpen, setMarkSignedOpen] = useState(false)
  const [markSignedProposal, setMarkSignedProposal] = useState<ProposalRecord | null>(null)
  const [markSignedName, setMarkSignedName] = useState('')
  const [markSignedDate, setMarkSignedDate] = useState(() => new Date().toISOString().slice(0, 10))
  const [isMarkingSigned, setIsMarkingSigned] = useState(false)

  const user = useAuthStore((s) => s.user)
  const userRole = user?.role || ''
  const isManagerOrAbove = userRole === 'MANAGER' || userRole === 'ADMIN' || userRole === 'SUPERADMIN'
  const isAdmin = userRole === 'ADMIN' || userRole === 'SUPERADMIN'

  // Delete proposal state
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  // Approve estimate state
  const [approvingId, setApprovingId] = useState<string | null>(null)
  // Inline error message shown as a bottom toast when a row action fails.
  // Replaces window.alert so Chrome's "Don't allow this site to send dialogs"
  // toggle can't silently swallow the backend's reason (e.g. an old self-approval
  // 403 that would otherwise look like the click did nothing).
  const [actionError, setActionError] = useState<string | null>(null)
  useEffect(() => {
    if (!actionError) return
    const t = setTimeout(() => setActionError(null), 6000)
    return () => clearTimeout(t)
  }, [actionError])

  // Pending row 3-dot menu state
  const [pendingMenuOpenId, setPendingMenuOpenId] = useState<string | null>(null)
  // Anchor for the pending row's 3-dot menu. Attached only to the open row's
  // button (see conditional ref below); the menu itself renders via PortalDropdown
  // so the actions column's horizontal-scroll wrapper can't clip it.
  const pendingMenuAnchorRef = useRef<HTMLButtonElement>(null)

  // Close row menu on outside click
  useEffect(() => {
    if (!menuOpenId) return
    function handleClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpenId(null)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [menuOpenId])

  async function handleMarkSent(e: React.MouseEvent, proposalId: string) {
    e.stopPropagation()
    setPendingMenuOpenId(null)
    try {
      await proposalsService.markAsSent(proposalId)
      setProposals((prev) =>
        prev.map((p) =>
          p.id === proposalId
            ? { ...p, status: 'sent_to_client', sentToClientAt: new Date().toISOString() } as ProposalRecord
            : p
        )
      )
    } catch (err) {
      console.error('Failed to mark proposal as sent:', err)
    }
  }

  // Fetch proposals from API
  useEffect(() => {
    let cancelled = false
    async function load() {
      try {
        const result = await proposalsService.list(0, take)
        if (!cancelled) {
          setProposals(result.proposals)
          setTotal(result.total ?? result.proposals.length)
        }
      } catch (err) {
        console.error('Failed to load proposals:', err)
        if (!cancelled) setLoadError('Failed to load proposals. Please refresh the page.')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [])

  async function handleShowAll() {
    if (loadingAll || total <= take) return
    setLoadingAll(true)
    try {
      const result = await proposalsService.list(0, total)
      setProposals(result.proposals)
      setTotal(result.total ?? result.proposals.length)
      setTake(total) // only after success — a failure leaves the button usable
    } catch (err) {
      console.error('Failed to load all proposals:', err)
    } finally {
      setLoadingAll(false)
    }
  }

  // Auto-open "Ready to Send" section when navigated from a notification
  // Auto-switch to pending_approval tab when navigated from dashboard
  useEffect(() => {
    if (searchParams.get('ready') === '1') {
      setPendingOpen(true)
    }
    if (searchParams.get('tab') === 'pending_approval') {
      setActiveTab('pending_approval')
    }
  }, [searchParams])

  // Map to display statuses
  const resolved = proposals.map((p) => ({
    record: p,
    displayStatus: mapStatus(p),
    name: p.title || p.clientName || 'Untitled Proposal',
    client: p.clientName || '',
    email: p.clientEmail || '',
    estimator: p.createdByUser?.displayName || '',
  }))

  // Split: submitted-for-approval proposals get their own section/tab
  const pendingApprovalProposals = resolved.filter(
    (p) => p.record.estimate?.status === 'submitted_for_approval'
  )
  // "Ready to Send" = draft proposals whose estimate is approved (not awaiting approval)
  const pendingProposals = resolved.filter(
    (p) => p.displayStatus === 'draft' && p.record.estimate?.status !== 'submitted_for_approval'
  )
  const sentProposals = resolved.filter((p) => p.displayStatus !== 'draft')

  // Role-based visibility
  const visiblePending = isManagerOrAbove
    ? pendingProposals
    : pendingProposals.filter((p) => p.record.createdByUserId === user?.id || p.record.estimate?.createdByUserId === user?.id)

  const visiblePendingApproval = isManagerOrAbove
    ? pendingApprovalProposals
    : pendingApprovalProposals.filter((p) => p.record.createdByUserId === user?.id || p.record.estimate?.createdByUserId === user?.id)

  const matchesSearch = (p: typeof resolved[0]) => {
    if (!search) return true
    const q = search.toLowerCase()
    return (
      p.name.toLowerCase().includes(q) ||
      p.email.toLowerCase().includes(q) ||
      p.client.toLowerCase().includes(q) ||
      // The estimator is a visible column ("Created By") and was already resolved
      // for it, but search never looked at it — so searching a colleague's name
      // returned nothing.
      p.estimator.toLowerCase().includes(q) ||
      (p.record.proposalNumber || '').toLowerCase().includes(q)
    )
  }

  // Search also covers the Ready to Send section
  const shownPending = visiblePending.filter(matchesSearch)

  // Filter by tab and search.
  //
  // Every other tab lists proposals that went out to a customer, so they work off
  // `sentProposals`. Revised works off ALL of them: finishing a revision resets the
  // proposal to draft until it is re-sent, so the ones revised most recently would
  // otherwise be missing from the very tab meant for finding revised proposals.
  // They also still appear in Ready to Send above, which is where they are worked.
  const filtered = (activeTab === 'revised' ? resolved : sentProposals).filter((p) => {
    if (!matchesSearch(p)) return false
    if (activeTab === 'all') return true
    if (activeTab === 'revised') return isRevisedProposal(p.record)
    return p.displayStatus === activeTab
  })

  const visible = isManagerOrAbove
    ? filtered
    : filtered.filter((p) => p.record.createdByUserId === user?.id || p.record.estimate?.createdByUserId === user?.id)

  const totalPages = Math.ceil(visible.length / PAGE_SIZE)
  const pagedVisible = visible.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  // Shown beside the status tabs, so a search that returns nothing reads as
  // "0 matches" rather than a broken page. It counts the proposals table (the
  // current tab); the approval tab renders its own list, so it counts that.
  const resultCount = activeTab === 'pending_approval' ? visiblePendingApproval.length : visible.length
  const resultLabel = search
    ? `${resultCount} ${resultCount === 1 ? 'match' : 'matches'}`
    : `${resultCount} ${resultCount === 1 ? 'proposal' : 'proposals'}`

  // Open send confirmation modal
  function handleSendClick(e: React.MouseEvent, p: typeof resolved[0]) {
    e.stopPropagation()
    setSendingProposal(p.record)
    setEmailRecipients([p.record.clientEmail].filter(Boolean))
    setSendModalOpen(true)
  }

  // Confirm send
  async function handleConfirmSend() {
    if (!sendingProposal) return
    setIsSending(true)

    try {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      const validEmails = emailRecipients.map(e => e.trim()).filter(e => emailRegex.test(e))
      if (validEmails.length === 0) {
        alert('Please add at least one email address.')
        setIsSending(false)
        return
      }
      await proposalsService.send(sendingProposal.id, validEmails)
      setProposals((prev) =>
        prev.map((p) =>
          p.id === sendingProposal.id
            ? { ...p, status: 'sent_to_client', sentToClientAt: new Date().toISOString() } as ProposalRecord
            : p
        )
      )
      setSendModalOpen(false)
      setSendingProposal(null)
    } catch (err) {
      console.error('Failed to send proposal:', err)
      alert('Failed to send proposal. Please try again.')
    } finally {
      setIsSending(false)
    }
  }

  // Open Mark as Signed confirmation first
  function handleMarkSignedClick(record: ProposalRecord) {
    setMenuOpenId(null)
    setMarkSignedConfirmRecord(record)
    setMarkSignedConfirmOpen(true)
  }

  // User confirmed — open the details dialog
  function handleMarkSignedConfirmed() {
    if (!markSignedConfirmRecord) return
    setMarkSignedConfirmOpen(false)
    setMarkSignedProposal(markSignedConfirmRecord)
    setMarkSignedName(markSignedConfirmRecord.clientName || '')
    setMarkSignedDate(new Date().toISOString().slice(0, 10))
    setMarkSignedOpen(true)
    setMarkSignedConfirmRecord(null)
  }

  // Confirm mark as signed
  async function handleConfirmMarkSigned() {
    if (!markSignedProposal || !markSignedName.trim()) return
    setIsMarkingSigned(true)
    try {
      await proposalsService.markAsSigned(markSignedProposal.id, markSignedName.trim(), markSignedDate)
      setProposals((prev) =>
        prev.map((p) =>
          p.id === markSignedProposal.id
            ? { ...p, status: 'signed', signedAt: new Date(markSignedDate).toISOString(), signatureName: markSignedName.trim() } as ProposalRecord
            : p
        )
      )
      setMarkSignedOpen(false)
      setMarkSignedProposal(null)
    } catch (err) {
      console.error('Failed to mark proposal as signed:', err)
      alert('Failed to mark proposal as signed. Please try again.')
    } finally {
      setIsMarkingSigned(false)
    }
  }

  // Start a revision
  async function handleRevise(e: React.MouseEvent, record: ProposalRecord) {
    e.stopPropagation()
    setMenuOpenId(null)
    try {
      const result = await proposalsService.startRevision(record.id)
      // Navigate to the cloned estimate in revision mode
      navigate(`/estimates/${result.revisionEstimate.id}`)
    } catch (err) {
      console.error('Failed to start revision:', err)
      alert('Failed to start revision. Please try again.')
    }
  }

  // Delete proposal
  async function handleDeleteProposal(id: string) {
    if (deletingId) return
    setDeletingId(id)
    try {
      await proposalsService.remove(id)
      setProposals((prev) => prev.filter((p) => p.id !== id))
    } catch (err: any) {
      alert(err?.response?.data?.message || 'Failed to delete proposal. Please try again.')
    } finally {
      setDeletingId(null)
      setDeleteConfirmId(null)
    }
  }

  // Approve estimate from proposals tab
  async function handleApprove(record: ProposalRecord) {
    if (approvingId) return
    setApprovingId(record.id)
    setActionError(null)
    try {
      await estimatesService.updateStatus(record.estimateId, 'approved')
      setProposals((prev) =>
        prev.map((p) =>
          p.id === record.id && p.estimate
            ? { ...p, estimate: { ...p.estimate, status: 'approved' } } as ProposalRecord
            : p
        )
      )
    } catch (err: any) {
      setActionError(err?.response?.data?.message || 'Failed to approve. Please try again.')
    } finally {
      setApprovingId(null)
    }
  }

  // Download PDF
  async function handleDownloadPdf(e: React.MouseEvent, record: ProposalRecord) {
    e.stopPropagation()
    setMenuOpenId(null)
    try {
      const blob = await proposalsService.downloadPdf(record.id)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${record.proposalNumber || 'proposal'}.pdf`
      a.click()
      URL.revokeObjectURL(url)
    } catch (err) {
      console.error('Failed to download PDF:', err)
      alert('Failed to download PDF. Please try again.')
    }
  }

  if (loading) {
    return (
      <div>
        <div className="est-list-head">
          <h1 className="est-list-title">Proposals</h1>
        </div>
        <PageLoader />
      </div>
    )
  }

  if (loadError) {
    return (
      <div>
        <div className="est-list-head">
          <h1 className="est-list-title">Proposals</h1>
        </div>
        <div style={{ padding: '48px', textAlign: 'center' }}>
          <p style={{ fontWeight: 500, color: 'var(--red)' }}>{loadError}</p>
        </div>
      </div>
    )
  }

  return (
    <div>
      {/* Page Header */}
      <div className="est-list-head">
        <h1 className="est-list-title">Proposals</h1>
      </div>

      {/* Search — first thing on the page, always visible.
          It used to sit BELOW the collapsible "Ready to Send" section, so it was
          only on screen when that section was folded away; with it open, the list
          pushed the search down and often off the screen. Up here it never depends
          on that section. It searches both lists: Ready to Send below (which opens
          itself while a search is active, then folds back when cleared) and the
          proposals table. */}
      <div className="prop-filter-bar prop-search-top">
        <div className="prop-filter-row">
          <div className="prop-filter-search">
            <span className="prop-filter-search-icon">
              <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <circle cx="11" cy="11" r="8" />
                <path d="M21 21l-4.35-4.35" />
              </svg>
            </span>
            <input
              type="text"
              className="prop-filter-input"
              placeholder="Search by proposal #, project, client, estimator or email..."
              aria-label="Search proposals"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1) }}
            />
            {search && (
              <button
                type="button"
                className="prop-filter-clear"
                aria-label="Clear search"
                onClick={() => { setSearch(''); setPage(1) }}
              >
                <svg fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── Pending Proposals (Ready to Send) ── */}
      {shownPending.length > 0 && (
        <div className="prop-pending-section">
          <div className="prop-pending-header prop-pending-toggle" onClick={() => setPendingOpen(!pendingOpen)}>
            <div className="prop-pending-icon">
              <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M22 2L11 13" /><path d="M22 2L15 22L11 13L2 9L22 2Z" />
              </svg>
            </div>
            <div style={{ flex: 1 }}>
              <h2 className="prop-pending-title">Ready to Send</h2>
              <p className="prop-pending-subtitle">{shownPending.length} proposal{shownPending.length !== 1 ? 's' : ''} approved and waiting to be sent</p>
            </div>
            <svg className={`prop-pending-chevron ${pendingOpen || search ? 'open' : ''}`} width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M6 9l6 6 6-6" />
            </svg>
          </div>

          {(pendingOpen || search !== '') && (
            <div className="prop-pending-scroll">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Proposal ID</th>
                    <th>Proposal</th>
                    <th>Client</th>
                    <th>Recipient</th>
                    {isManagerOrAbove && <th>Created by</th>}
                    <th>Created</th>
                    <th className="prop-actions-col"></th>
                  </tr>
                </thead>
                <tbody>
                  {shownPending.map((p) => (
                    <tr
                      key={p.record.id}
                      className="prop-row"
                      onClick={() => navigate(`/proposals/preview?estimateId=${p.record.estimateId}&from=/proposals`)}
                    >
                      <td style={{ whiteSpace: 'nowrap' }}><span className="prop-id">{p.record.proposalNumber}</span></td>
                      <td style={{ fontWeight: 500, color: 'var(--s900)', whiteSpace: 'nowrap', maxWidth: '260px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>{p.client}</td>
                      <td style={{ whiteSpace: 'nowrap', maxWidth: '220px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.email}</td>
                      {isManagerOrAbove && <td style={{ whiteSpace: 'nowrap' }}>{p.estimator || '—'}</td>}
                      <td style={{ whiteSpace: 'nowrap' }}>{formatDateShort(p.record.createdAt)}</td>
                      <td className="prop-actions-col">
                        <div className="prop-actions-wrap">
                          {isAdmin && (
                            <button
                              className="est-dup-icon-btn"
                              onClick={(e) => { e.stopPropagation(); setMenuOpenId(null); setDeleteConfirmId(p.record.id) }}
                              title="Delete proposal"
                              style={{ color: 'var(--red)' }}
                            >
                              <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
                                <polyline points="3 6 5 6 21 6" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M10 11v6M14 11v6" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </button>
                          )}
                          <button
                            className="prop-revise-btn"
                            onClick={(e) => handleRevise(e, p.record)}
                            title="Revise this proposal"
                          >
                            <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                            </svg>
                            Revise
                          </button>
                          <button
                            className="prop-send-btn"
                            onClick={(e) => handleSendClick(e, p)}
                          >
                            <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                              <path d="M22 2L11 13" /><path d="M22 2L15 22L11 13L2 9L22 2Z" />
                            </svg>
                            Send
                          </button>
                          <button
                            className="prop-row-menu-btn"
                            ref={pendingMenuOpenId === p.record.id ? pendingMenuAnchorRef : null}
                            onClick={(e) => { e.stopPropagation(); setPendingMenuOpenId(pendingMenuOpenId === p.record.id ? null : p.record.id) }}
                            title="More options"
                          >
                            <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
                              <circle cx="12" cy="5" r="1.5"/><circle cx="12" cy="12" r="1.5"/><circle cx="12" cy="19" r="1.5"/>
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pending row 3-dot menu — portaled so the actions column's
              horizontal-scroll wrapper (overflow-x:auto) can't clip it. */}
          <PortalDropdown
            open={pendingMenuOpenId !== null}
            anchorRef={pendingMenuAnchorRef}
            onClose={() => setPendingMenuOpenId(null)}
            placement="bottom-end"
            className="prop-row-menu"
          >
            {(() => {
              const openRecord = shownPending.find((p) => p.record.id === pendingMenuOpenId)?.record
              if (!openRecord) return null
              return (
                <>
                  <button
                    className="prop-row-menu-item"
                    onClick={(e) => handleMarkSent(e, openRecord.id)}
                  >
                    <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                    Manually Sent
                  </button>
                  <button
                    className="prop-row-menu-item"
                    onClick={(e) => { e.stopPropagation(); handleMarkSignedClick(openRecord) }}
                  >
                    <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                    Mark as Signed
                  </button>
                </>
              )
            })()}
          </PortalDropdown>
        </div>
      )}

      {/* Status tabs + count — these filter the proposals table directly below,
          so they sit with it rather than up with the search. The count is that
          table's (the current tab, narrowed by any search); Ready to Send shows
          its own count in its header. */}
      <div className="prop-filter-bar prop-tabs-bar" style={{ marginTop: shownPending.length > 0 ? '24px' : '0' }}>
        <div className="prop-tabs-row">
          <div className="est-list-tabs">
            {TAB_CONFIG.map((tab) => {
              if (tab.key === 'pending_approval' && !isManagerOrAbove) return null
              return (
                <button
                  key={tab.key}
                  className={`est-list-tab ${activeTab === tab.key ? 'active' : ''}`}
                  onClick={() => { setActiveTab(tab.key); setPage(1) }}
                >
                  {tab.label}
                  {tab.key === 'pending_approval' && visiblePendingApproval.length > 0 && (
                    <span className="est-tab-badge waiting">{visiblePendingApproval.length}</span>
                  )}
                </button>
              )
            })}
          </div>
          <span className="prop-filter-count">{resultLabel}</span>
        </div>
      </div>

      <TruncationNotice shown={proposals.length} total={total} onShowAll={handleShowAll} loading={loadingAll} />

      {/* Proposals Table */}
      <div className="card" style={{ marginTop: '15px', overflow: 'visible' }}>
        {activeTab === 'pending_approval' ? (
          visiblePendingApproval.length === 0 ? (
            <div style={{ padding: '48px', textAlign: 'center' }}>
              <svg style={{ width: '48px', height: '48px', margin: '0 auto 12px', color: 'var(--s300)' }} fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 0 1-2-2V5a2 2 0 0 1-2-2h5.586a1 1 0 0 1 .707.293l5.414 5.414a1 1 0 0 1 .293.707V19a2 2 0 0 1-2 2Z" />
              </svg>
              <p style={{ fontWeight: 500, color: 'var(--s500)' }}>No proposals awaiting approval</p>
              <p style={{ fontSize: '13px', color: 'var(--s400)', marginTop: '4px' }}>Proposals submitted for approval will appear here.</p>
            </div>
          ) : (
            <table className="tbl">
              <thead>
                <tr>
                  <th>Proposal ID</th>
                  <th>Proposal</th>
                  <th>Client</th>
                  <th>Estimator</th>
                  <th>Submitted</th>
                  <th style={{ width: '160px' }}></th>
                </tr>
              </thead>
              <tbody>
                {visiblePendingApproval.map((p) => (
                  <tr
                    key={p.record.id}
                    className="prop-row"
                    onClick={() => navigate(`/estimates/${p.record.estimateId}`)}
                  >
                    <td style={{ whiteSpace: 'nowrap' }}><span className="prop-id">{p.record.proposalNumber}</span></td>
                    <td style={{ fontWeight: 500, color: 'var(--s900)' }}>{p.name}</td>
                    <td>{p.client}</td>
                    <td>{p.estimator}</td>
                    <td>{formatDateShort(p.record.updatedAt)}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end', alignItems: 'center' }}>
                        <button
                          className="est-review-btn"
                          onClick={() => navigate(`/estimates/${p.record.estimateId}`)}
                        >
                          Review
                        </button>
                        <button
                          className="est-approve-btn"
                          onClick={() => handleApprove(p.record)}
                          disabled={approvingId === p.record.id}
                        >
                          {approvingId === p.record.id ? 'Approving…' : 'Approve'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        ) : pagedVisible.length === 0 && visible.length === 0 ? (
          <div style={{ padding: '48px', textAlign: 'center' }}>
            <svg style={{ width: '48px', height: '48px', margin: '0 auto 12px', color: 'var(--s300)' }} fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
              <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 0 1-2-2V5a2 2 0 0 1-2-2h5.586a1 1 0 0 1 .707.293l5.414 5.414a1 1 0 0 1 .293.707V19a2 2 0 0 1-2 2Z" />
            </svg>
            <p style={{ fontWeight: 500, color: 'var(--s500)' }}>No proposals found</p>
            <p style={{ fontSize: '13px', color: 'var(--s400)', marginTop: '4px' }}>
              {search ? 'Try a different search term.' : 'Sent proposals will appear here.'}
            </p>
          </div>
        ) : (
          <>
          <table className="tbl">
            <thead>
              <tr>
                <th>Proposal ID</th>
                <th>Proposal</th>
                <th>Client</th>
                {isManagerOrAbove && <th>Created by</th>}
                <th>Date Sent</th>
                <th>Status</th>
                <th style={{ width: '120px' }}></th>
              </tr>
            </thead>
            <tbody>
              {pagedVisible.map((p) => {
                const isExpired = p.displayStatus === 'expired'
                void isExpired
                return (
                  <tr
                    key={p.record.id}
                    className="prop-row"
                    onClick={() => navigate(`/proposals/preview?estimateId=${p.record.estimateId}&from=/proposals`)}
                  >
                    <td>
                      <span className="prop-id">{p.record.proposalNumber}</span>
                      {/* "Rev 1" is the FIRST revision — the stored number is a
                          version, where the original is 1. See lib/revision-label. */}
                      {revisionCount(p.record.revisionNumber) > 0 && (
                        <span className="prop-version-badge">Rev {revisionCount(p.record.revisionNumber)}</span>
                      )}
                    </td>
                    <td style={{ fontWeight: 500, color: 'var(--s900)' }}>{p.name}</td>
                    <td>{p.client}</td>
                    {isManagerOrAbove && <td style={{ whiteSpace: 'nowrap' }}>{p.estimator || '—'}</td>}
                    <td>{formatDate(p.record.sentToClientAt)}</td>
                    <td>
                      <span className={`prop-status ${p.displayStatus}`}>
                        {STATUS_LABELS[p.displayStatus]}
                      </span>
                    </td>
                    <td style={{ textAlign: 'right', position: 'relative' }}>
                      <button
                        className="prop-row-menu-btn"
                        onClick={(e) => {
                          e.stopPropagation()
                          setMenuOpenId(menuOpenId === p.record.id ? null : p.record.id)
                        }}
                      >
                        <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                          <circle cx="8" cy="3" r="1.5" />
                          <circle cx="8" cy="8" r="1.5" />
                          <circle cx="8" cy="13" r="1.5" />
                        </svg>
                      </button>
                      {menuOpenId === p.record.id && (
                        <div className="prop-row-menu" ref={menuRef}>
                          <button
                            className="prop-row-menu-item"
                            onClick={(e) => { e.stopPropagation(); handleSendClick(e, p) }}
                          >
                            <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                              <path d="M22 2L11 13" /><path d="M22 2L15 22L11 13L2 9L22 2Z" />
                            </svg>
                            Resend
                          </button>
                          <button
                            className="prop-row-menu-item"
                            onClick={(e) => { e.stopPropagation(); handleDownloadPdf(e, p.record) }}
                          >
                            <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                              <polyline points="7 10 12 15 17 10" />
                              <line x1="12" y1="15" x2="12" y2="3" />
                            </svg>
                            Download PDF
                          </button>
                          {p.displayStatus !== 'signed' && (
                            <button
                              className="prop-row-menu-item"
                              onClick={(e) => handleRevise(e, p.record)}
                            >
                              <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                              </svg>
                              Revise
                            </button>
                          )}
                          {p.displayStatus !== 'signed' && (
                            <button
                              className="prop-row-menu-item"
                              onClick={(e) => { e.stopPropagation(); handleMarkSignedClick(p.record) }}
                            >
                              <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                <path d="M20 6L9 17l-5-5" />
                              </svg>
                              Mark as Signed
                            </button>
                          )}
                          {p.displayStatus === 'signed' && (
                            <button
                              className="prop-row-menu-item"
                              onClick={(e) => { e.stopPropagation(); setMenuOpenId(null); alert('Change order feature coming soon.') }}
                            >
                              <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                <polyline points="14 2 14 8 20 8" />
                                <line x1="12" y1="18" x2="12" y2="12" />
                                <line x1="9" y1="15" x2="15" y2="15" />
                              </svg>
                              Add Change Order
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          <Pagination
            page={page}
            totalPages={totalPages}
            totalItems={visible.length}
            pageSize={PAGE_SIZE}
            onPageChange={setPage}
          />
          </>
        )}
      </div>

      {/* Delete Proposal Confirmation Dialog */}
      {deleteConfirmId && (
        <div className="est-modal-overlay" onClick={() => !deletingId && setDeleteConfirmId(null)}>
          <div className="est-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '420px' }}>
            <div className="est-modal-header">
              <h3 className="est-modal-title">Delete Proposal</h3>
              <button className="est-modal-close" onClick={() => !deletingId && setDeleteConfirmId(null)}>
                <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M6 18L18 6M6 6l12 12"/></svg>
              </button>
            </div>
            <div style={{ padding: '20px 24px' }}>
              <p style={{ color: 'var(--s700)', fontSize: '14px', lineHeight: '1.5' }}>
                Are you sure you want to delete this proposal? This action cannot be undone.
              </p>
            </div>
            <div style={{ padding: '0 24px 20px', display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                className="btn-secondary"
                onClick={() => setDeleteConfirmId(null)}
                disabled={!!deletingId}
              >
                Cancel
              </button>
              <button
                className="btn"
                style={{ background: 'var(--red)', borderColor: 'var(--red)' }}
                onClick={() => handleDeleteProposal(deleteConfirmId)}
                disabled={!!deletingId}
              >
                {deletingId ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Send Proposal Modal */}
      {sendModalOpen && sendingProposal && (
        <div className="est-modal-overlay" onClick={() => !isSending && setSendModalOpen(false)}>
          <div className="est-modal" onClick={(e) => e.stopPropagation()}>
            <div className="est-modal-header">
              <h3 className="est-modal-title">Send Proposal</h3>
              <button className="est-modal-close" onClick={() => !isSending && setSendModalOpen(false)}>
                <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M6 18L18 6M6 6l12 12"/></svg>
              </button>
            </div>
            <div className="est-modal-body">
              <p className="prop-send-desc">
                A branded email with a link to view and sign the proposal will be sent to the following recipients:
              </p>

              {/* Email recipients list */}
              <div className="prop-send-extras">
                {emailRecipients.map((email, i) => (
                  <div key={i} className="prop-send-extra-row">
                    <div className="prop-send-extra-avatar">
                      {(email.charAt(0) || '?').toUpperCase()}
                    </div>
                    <input
                      type="email"
                      className="prop-send-extra-input"
                      placeholder="email@example.com"
                      value={email}
                      onChange={(e) => {
                        const updated = [...emailRecipients]
                        updated[i] = e.target.value
                        setEmailRecipients(updated)
                      }}
                      disabled={isSending}
                    />
                    <button
                      className="prop-send-extra-remove"
                      onClick={() => setEmailRecipients(emailRecipients.filter((_, idx) => idx !== i))}
                      disabled={isSending}
                      title="Remove"
                    >
                      <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M6 18L18 6M6 6l12 12"/></svg>
                    </button>
                  </div>
                ))}
              </div>

              <button
                className="prop-send-add-email"
                onClick={() => setEmailRecipients([...emailRecipients, ''])}
                disabled={isSending}
              >
                <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M12 5v14M5 12h14"/></svg>
                Add Another Email Address
              </button>

              <div className="prop-send-details">
                <div className="prop-send-detail-row">
                  <span className="prop-send-detail-label">Proposal</span>
                  <span>{sendingProposal.title || sendingProposal.clientName}</span>
                </div>
                <div className="prop-send-detail-row">
                  <span className="prop-send-detail-label">Proposal ID</span>
                  <span className="prop-id">{sendingProposal.proposalNumber}</span>
                </div>
              </div>
            </div>
            <div className="est-modal-footer">
              <button className="est-modal-btn-cancel" onClick={() => !isSending && setSendModalOpen(false)} disabled={isSending}>
                Cancel
              </button>
              <button
                className="prop-send-confirm-btn"
                onClick={handleConfirmSend}
                disabled={isSending}
              >
                {isSending ? (
                  <>
                    <span className="prop-send-spinner" />
                    Sending...
                  </>
                ) : (
                  <>
                    <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path d="M22 2L11 13" /><path d="M22 2L15 22L11 13L2 9L22 2Z" />
                    </svg>
                    Confirm & Send
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mark as Signed — Confirmation Dialog */}
      {markSignedConfirmOpen && markSignedConfirmRecord && (
        <div className="est-modal-overlay" onClick={() => setMarkSignedConfirmOpen(false)}>
          <div className="est-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '420px' }}>
            <div className="est-modal-header">
              <h3 className="est-modal-title">Mark as Signed</h3>
              <button className="est-modal-close" onClick={() => setMarkSignedConfirmOpen(false)}>
                <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M6 18L18 6M6 6l12 12"/></svg>
              </button>
            </div>
            <div style={{ padding: '20px 24px' }}>
              <p style={{ color: 'var(--s700)', fontSize: '14px', lineHeight: '1.5' }}>
                Are you sure you want to mark this proposal as signed?
              </p>
            </div>
            <div style={{ padding: '0 24px 20px', display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button className="btn-secondary" onClick={() => setMarkSignedConfirmOpen(false)}>
                Cancel
              </button>
              <button className="btn" onClick={handleMarkSignedConfirmed}>
                Yes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mark as Signed Modal */}
      {markSignedOpen && markSignedProposal && (
        <div className="est-modal-overlay" onClick={() => !isMarkingSigned && setMarkSignedOpen(false)}>
          <div className="est-modal prop-mark-signed-modal" onClick={(e) => e.stopPropagation()}>
            <div className="est-modal-header">
              <h3 className="est-modal-title">Mark as Signed</h3>
              <button className="est-modal-close" onClick={() => !isMarkingSigned && setMarkSignedOpen(false)}>
                <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M6 18L18 6M6 6l12 12"/></svg>
              </button>
            </div>
            <div className="est-modal-body">
              <p className="prop-mark-signed-desc">
                Record that this proposal was signed physically (e.g. printed and mailed back).
              </p>

              <div className="prop-mark-signed-info">
                <div className="prop-send-detail-row">
                  <span className="prop-send-detail-label">Proposal</span>
                  <span>{markSignedProposal.title || markSignedProposal.clientName}</span>
                </div>
                <div className="prop-send-detail-row">
                  <span className="prop-send-detail-label">Proposal ID</span>
                  <span className="prop-id">{markSignedProposal.proposalNumber}</span>
                </div>
              </div>

              <div className="prop-mark-signed-fields">
                <div className="prop-mark-signed-field">
                  <label className="prop-mark-signed-label">Signer Name</label>
                  <input
                    type="text"
                    className="prop-mark-signed-input"
                    placeholder="Who signed the proposal?"
                    value={markSignedName}
                    onChange={(e) => setMarkSignedName(e.target.value)}
                    disabled={isMarkingSigned}
                    autoFocus
                  />
                </div>
                <div className="prop-mark-signed-field">
                  <label className="prop-mark-signed-label">Date Signed</label>
                  <input
                    type="date"
                    className="prop-mark-signed-input"
                    value={markSignedDate}
                    onChange={(e) => setMarkSignedDate(e.target.value)}
                    disabled={isMarkingSigned}
                  />
                </div>
              </div>
            </div>
            <div className="est-modal-footer">
              <button className="est-modal-btn-cancel" onClick={() => !isMarkingSigned && setMarkSignedOpen(false)} disabled={isMarkingSigned}>
                Cancel
              </button>
              <button
                className="prop-mark-signed-confirm"
                onClick={handleConfirmMarkSigned}
                disabled={isMarkingSigned || !markSignedName.trim()}
              >
                {isMarkingSigned ? (
                  <>
                    <span className="prop-send-spinner" />
                    Saving...
                  </>
                ) : (
                  <>
                    <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                    Confirm Signed
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bottom toast for row-action failures (approve, etc.). Non-modal so it can't be
          suppressed by Chrome's per-tab dialog blocker the way window.alert can. */}
      {actionError && <div className="usr-toast usr-toast-error">{actionError}</div>}
    </div>
  )
}
